import { WS_BASE } from '../config';
class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectInterval: number = 5000; // 5 seconds
  private reconnectTimer: NodeJS.Timeout | null = null;
  private subscribers: Map<string, (data: any) => void> = new Map();

  constructor(private url: string) {}

  connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        if (this.reconnectTimer) {
          clearInterval(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.ws?.close();
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearInterval(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  subscribe(eventType: string, callback: (data: any) => void): () => void {
    this.subscribers.set(eventType, callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.delete(eventType);
    };
  }

  private handleMessage(data: any): void {
    const callback = this.subscribers.get(data.type);
    if (callback) {
      callback(data);
    }

    // Handle announcements globally
    if (data.type === 'announcement_started' || (data.type === 'new_event' && data.event_type === 'announcement_started')) {
      const annCallback = this.subscribers.get('announcement_started');
      if (annCallback && data.type !== 'announcement_started') {
        annCallback(data);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('astravision_ws_announcement', { detail: data }));
      }
    }

    // Handle specific camera status updates
    if (data.type === 'camera_status_update') {
      const statusCallback = this.subscribers.get('camera_status');
      if (statusCallback) {
        statusCallback(data);
      }
    }
  }

  private scheduleReconnect(): void {
    if (!this.reconnectTimer) {
      this.reconnectTimer = setInterval(() => {
        console.log('Attempting to reconnect WebSocket...');
        this.connect();
      }, this.reconnectInterval);
    }
  }

  send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket is not connected. Message not sent:', data);
    }
  }
}

// Create singleton instance
const websocketService = new WebSocketService(`${WS_BASE}/api/v1/ws`);

export default websocketService;
